import React, { useState, useRef } from 'react';
import { Plus, Edit, Trash2, Upload, X, Download, Save } from 'lucide-react';
import { useStore } from '../../stores/appStore';
import { Moto } from '../../types';
import { generateId, formatPrice } from '../../utils';

export const MotoManager: React.FC = () => {
  const { motos, addMoto, updateMoto, deleteMoto, setMotos } = useStore();
  const [showForm, setShowForm] = useState(false);
  const [editingMoto, setEditingMoto] = useState<Moto | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const backupInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    nome: '',
    modelo: '',
    ano: new Date().getFullYear(),
    cilindrada: 0,
    consumo: '',
    tanque: '',
    peso: '',
    preco: 0,
    precoPromocional: 0,
    estoque: 0,
    categoria: 'Street',
    descricao: '',
    imagens: [] as string[],
    destaque: false,
  });

  const handleExportBackup = () => {
    const dataStr = JSON.stringify(motos, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `backup_motos_${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const handleImportBackup = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileReader = new FileReader();
    if (e.target.files && e.target.files[0]) {
      fileReader.readAsText(e.target.files[0], "UTF-8");
      fileReader.onload = (event) => {
        if (event.target?.result) {
          try {
            const parsedData = JSON.parse(event.target.result as string);
            if (Array.isArray(parsedData)) {
              if (confirm('Isso substituirá todas as motos atuais. Deseja continuar?')) {
                setMotos(parsedData);
                alert('Backup restaurado com sucesso!');
              }
            } else {
              alert('Arquivo de backup inválido.');
            }
          } catch (error) {
            alert('Erro ao ler arquivo de backup.');
          }
        }
      };
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const motoData: Moto = {
      ...formData,
      id: editingMoto?.id || generateId(),
      precoPromocional: formData.precoPromocional || undefined,
      visualizacoes: editingMoto?.visualizacoes || 0,
      createdAt: editingMoto?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    if (editingMoto) {
      updateMoto(editingMoto.id, motoData);
    } else {
      addMoto(motoData);
    }

    resetForm();
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      modelo: '',
      ano: new Date().getFullYear(),
      cilindrada: 0,
      consumo: '',
      tanque: '',
      peso: '',
      preco: 0,
      precoPromocional: 0,
      estoque: 0,
      categoria: 'Street',
      descricao: '',
      imagens: [],
      destaque: false,
    });
    setShowForm(false);
    setEditingMoto(null);
  };

  const handleEdit = (moto: Moto) => {
    setFormData({
      nome: moto.nome,
      modelo: moto.modelo,
      ano: moto.ano,
      cilindrada: moto.cilindrada,
      consumo: moto.consumo,
      tanque: moto.tanque,
      peso: moto.peso,
      preco: moto.preco,
      precoPromocional: moto.precoPromocional || 0,
      estoque: moto.estoque,
      categoria: moto.categoria,
      descricao: moto.descricao,
      imagens: moto.imagens,
      destaque: moto.destaque,
    });
    setEditingMoto(moto);
    setShowForm(true);
  };

  const handleDelete = (id: string) => {
    if (confirm('Tem certeza que deseja excluir esta moto?')) {
      deleteMoto(id);
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      Array.from(files).forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFormData(prev => ({
            ...prev,
            imagens: [...prev.imagens, reader.result as string]
          }));
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removeImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      imagens: prev.imagens.filter((_, i) => i !== index)
    }));
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold">Gerenciar Motos</h3>
        <div className="flex gap-2">
          <input
            type="file"
            ref={backupInputRef}
            onChange={handleImportBackup}
            accept=".json"
            className="hidden"
          />
          <button
            onClick={() => backupInputRef.current?.click()}
            className="flex items-center space-x-2 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
            title="Importar Backup"
          >
            <Upload size={20} />
            <span className="hidden md:inline">Restaurar</span>
          </button>
          <button
            onClick={handleExportBackup}
            className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
            title="Exportar Backup"
          >
            <Download size={20} />
            <span className="hidden md:inline">Backup</span>
          </button>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center space-x-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Plus size={20} />
            <span>Adicionar</span>
          </button>
        </div>
      </div>

      {/* Lista de Motos */}
      {!showForm && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {motos.map((moto) => (
            <div key={moto.id} className="border rounded-lg p-4">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h4 className="font-semibold text-lg">{moto.nome}</h4>
                  <p className="text-gray-600">{moto.modelo} - {moto.ano}</p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEdit(moto)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(moto.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                <div>
                  <span className="text-gray-500">Preço:</span>
                  <p className="font-semibold">{formatPrice(moto.preco)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Estoque:</span>
                  <p className="font-semibold">{moto.estoque} un</p>
                </div>
                <div>
                  <span className="text-gray-500">Visualizações:</span>
                  <p className="font-semibold">{moto.visualizacoes}</p>
                </div>
                <div>
                  <span className="text-gray-500">Destaque:</span>
                  <p className="font-semibold">{moto.destaque ? 'Sim' : 'Não'}</p>
                </div>
              </div>

              <div className="flex space-x-2">
                {moto.imagens.slice(0, 3).map((img, index) => (
                  <img
                    key={index}
                    src={img}
                    alt={`${moto.nome} ${index + 1}`}
                    className="w-16 h-16 object-cover rounded"
                  />
                ))}
                {moto.imagens.length > 3 && (
                  <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center text-sm text-gray-600">
                    +{moto.imagens.length - 3}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Formulário de Adição/Edição */}
      {showForm && (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome
              </label>
              <input
                type="text"
                value={formData.nome}
                onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Modelo
              </label>
              <input
                type="text"
                value={formData.modelo}
                onChange={(e) => setFormData(prev => ({ ...prev, modelo: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ano
              </label>
              <input
                type="number"
                value={formData.ano}
                onChange={(e) => setFormData(prev => ({ ...prev, ano: parseInt(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cilindrada (cc)
              </label>
              <input
                type="number"
                value={formData.cilindrada}
                onChange={(e) => setFormData(prev => ({ ...prev, cilindrada: parseFloat(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Consumo (km/l)
              </label>
              <input
                type="text"
                value={formData.consumo}
                onChange={(e) => setFormData(prev => ({ ...prev, consumo: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600"
                placeholder="Ex: 41 km/l"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tanque (litros)
              </label>
              <input
                type="text"
                value={formData.tanque}
                onChange={(e) => setFormData(prev => ({ ...prev, tanque: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600"
                placeholder="Ex: 13.5 litros"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Peso (kg)
              </label>
              <input
                type="text"
                value={formData.peso}
                onChange={(e) => setFormData(prev => ({ ...prev, peso: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600"
                placeholder="Ex: 135 kg"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Categoria
              </label>
              <select
                value={formData.categoria}
                onChange={(e) => setFormData(prev => ({ ...prev, categoria: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600"
              >
                <option value="Street">Street</option>
                <option value="Scooter">Scooter</option>
                <option value="Economia">Economia</option>
                <option value="Sport">Sport</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preço (R$)
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.preco}
                onChange={(e) => setFormData(prev => ({ ...prev, preco: parseFloat(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preço Promocional (R$)
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.precoPromocional}
                onChange={(e) => setFormData(prev => ({ ...prev, precoPromocional: parseFloat(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600"
                placeholder="Deixe 0 se não houver promoção"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Estoque
              </label>
              <input
                type="number"
                value={formData.estoque}
                onChange={(e) => setFormData(prev => ({ ...prev, estoque: parseInt(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descrição
            </label>
            <textarea
              value={formData.descricao}
              onChange={(e) => setFormData(prev => ({ ...prev, descricao: e.target.value }))}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Imagens da Moto
            </label>
            <div className="mb-4">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center space-x-2 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <Upload size={20} />
                <span>Adicionar Imagens</span>
              </button>
            </div>

            {formData.imagens.length > 0 && (
              <div className="grid grid-cols-4 gap-4">
                {formData.imagens.map((img, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={img}
                      alt={`Imagem ${index + 1}`}
                      className="w-full h-24 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute top-1 right-1 bg-red-600 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="destaque"
              checked={formData.destaque}
              onChange={(e) => setFormData(prev => ({ ...prev, destaque: e.target.checked }))}
              className="mr-2"
            />
            <label htmlFor="destaque" className="text-sm font-medium text-gray-700">
              Motocicleta em Destaque
            </label>
          </div>

          <div className="flex space-x-4">
            <button
              type="submit"
              className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg transition-colors"
            >
              {editingMoto ? 'Atualizar' : 'Adicionar'} Moto
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
          </div>
        </form>
      )}
    </div>
  );
};